/**
 * The console.
 *
 * Clients down the left, one build on the right. The build is the page: when one is running it
 * is what you are here for, and when none is, the invitation names the exact version the next
 * build would produce rather than saying "no data".
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiError, api,
  type Agent, type Client, type DeviceSlot, type Job, type Platforms, type Session,
  type Storage, type Template,
} from './api';
import { ClientPanel, RemoveClient } from './components/ClientPanel';
import { JobView } from './components/JobView';
import { BuildForm } from './components/BuildForm';
import { Macs } from './components/Macs';
import { NewClient } from './components/NewClient';
import { People } from './components/People';
import { ChoosePassword, SignIn } from './components/SignIn';
import { Register } from './components/Register';
import { StorageView } from './components/StorageView';

type View =
  | { kind: 'job'; id: number }
  | { kind: 'new-client' }
  | { kind: 'history' }
  | { kind: 'people' }
  | { kind: 'storage' }
  | { kind: 'macs' };

const REFRESH_MS = 4000;

/**
 * Light or dark, following the system until someone says otherwise.
 *
 * The button reports what it will switch to, so it has to know what is on screen now, not what
 * is stored: stored is 'system' until the first click, and flipping that against 'dark' makes
 * the first click a no-op on a dark machine.
 */
function useTheme(): [Showing, () => void] {
  const [choice, setChoice] = useState<string>(() => {
    try { return localStorage.getItem('wl-theme') ?? 'system'; } catch { return 'system'; }
  });
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
  );

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return;
    const listen = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', listen);
    return () => query.removeEventListener('change', listen);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (choice === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', choice);
    try { localStorage.setItem('wl-theme', choice); } catch { /* private window */ }
  }, [choice]);

  const showing: Showing = choice === 'system' ? (systemDark ? 'dark' : 'light') : (choice as Showing);
  return [showing, () => setChoice(showing === 'dark' ? 'light' : 'dark')];
}

type Showing = 'light' | 'dark';

function ago(timestamp: number): string {
  const seconds = Math.round((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
  return `${Math.floor(seconds / 86_400)} d ago`;
}

export function App() {
  const [clients, setClients] = useState<Client[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [devices, setDevices] = useState<DeviceSlot[]>([]);
  const [template, setTemplate] = useState<Template | null>(null);
  const [storage, setStorage] = useState<Storage | null>(null);
  const [platforms, setPlatforms] = useState<Platforms | null>(null);
  const [macs, setMacs] = useState<Agent[]>([]);
  const [view, setView] = useState<View>({ kind: 'history' });
  const [selected, setSelected] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);

  const showToast = useCallback((message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const reload = useCallback(async () => {
    try {
      const [nextClients, nextJobs, nextDevices, nextStorage, nextMacs] = await Promise.all([
        api.clients(), api.jobs(), api.devices(), api.storage(), api.agents(),
      ]);
      setClients(nextClients);
      setJobs(nextJobs);
      setDevices(nextDevices);
      setMacs(nextMacs);
      setStorage(nextStorage);
      setFatal(null);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) { setSession(null); return; }
      setFatal((error as Error).message);
    }
  }, []);

  useEffect(() => {
    api.me().then(setSession).catch(() => setSession(null)).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!session) return undefined;
    void reload();
    const timer = setInterval(() => void reload(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [reload, session]);

  // What this deployment can build does not change while the console is open.
  useEffect(() => {
    if (!session) return;
    api.platforms().then(setPlatforms).catch(() => setPlatforms(null));
  }, [session]);

  // Refs change when someone pushes, not every four seconds.
  useEffect(() => {
    if (!session) return;
    void api.template().then(setTemplate).catch(() => setTemplate(null));
  }, [session]);

  const [theme, toggleTheme] = useTheme();

  const build = useCallback(async (slug: string, options: Parameters<typeof api.build>[1]) => {
    try {
      const job = await api.build(slug, options);
      setView({ kind: 'job', id: job.id });
      showToast(`Build #${job.id} queued`);
      void reload();
    } catch (error) {
      showToast((error as Error).message);
    }
  }, [reload, showToast]);

  const visibleJobs = useMemo(
    () => (selected ? jobs.filter((job) => job.slug === selected) : jobs),
    [jobs, selected],
  );
  const client = useMemo(
    () => clients.find((candidate) => candidate.slug === selected) ?? null,
    [clients, selected],
  );

  if (checking) return <div className="signin-page" />;
  if (!session) return <SignIn onSignedIn={setSession} />;
  if (session.user.mustChangePassword) {
    return <ChoosePassword minLength={session.auth.minPasswordLength} onDone={() => setSession(null)} />;
  }

  const isAdmin = session.user.role === 'admin';
  const busy = devices.filter((device) => device.jobId !== null).length;
  const queued = jobs.filter((job) => job.state === 'queued').length;

  return (
    <div className="shell">
      <header className="topbar">
        <span className="wordmark">whitelabel <span>builds</span></span>
        <button type="button" className="plain"
                onClick={() => { setView({ kind: 'history' }); setSelected(null); }}>
          All builds
        </button>
        <button type="button" className="plain" onClick={() => setView({ kind: 'macs' })}>Macs</button>
        {isAdmin && (
          <>
            <button type="button" className="plain" onClick={() => setView({ kind: 'people' })}>People</button>
            <button type="button" className="plain" onClick={() => setView({ kind: 'storage' })}>Storage</button>
          </>
        )}

        <div className="topbar-right">
          {storage?.disk && (storage.queuePaused || storage.disk.usedFraction > 0.85) && (
            <span className={storage.queuePaused ? 'disk-note alarm' : 'disk-note warn'}>
              {storage.queuePaused
                ? `queue paused, ${(storage.disk.freeBytes / 1024 ** 3).toFixed(1)} GB free`
                : `disk ${Math.round(storage.disk.usedFraction * 100)}% full`}
            </span>
          )}
          {template?.repoUrl && (
            <span className="repo" title={`${template.repoUrl} — default branch ${template.defaultRef}`}>
              {template.repoUrl.replace(/^git@[^:]+:|^https?:\/\/[^/]+\//, '').replace(/\.git$/, '')}
            </span>
          )}
          <span className="capacity">
            {busy} of {devices.length} emulator{devices.length === 1 ? '' : 's'} busy
            {queued > 0 ? `, ${queued} waiting` : ''}
            <span className="slots">
              {devices.map((device) => (
                <span key={device.slot} className={`slot ${device.jobId ? 'busy' : ''}`}
                      title={`${device.avd} on port ${device.port}${device.jobId ? ` — build #${device.jobId}` : ' — free'}`} />
              ))}
            </span>
          </span>
          <span className="who">{session.user.email}</span>
          <button type="button" className="plain"
                  onClick={() => { void api.logout().finally(() => setSession(null)); }}>
            Sign out
          </button>
          <button type="button" className="plain" onClick={toggleTheme}>
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
        </div>
      </header>

      <nav className="rail">
        <div className="rail-head">
          <h2>Clients</h2>
          <span className="count">{clients.length}</span>
        </div>

        {clients.map((entry) => (
          <button
            type="button"
            key={entry.slug}
            className="client"
            aria-pressed={selected === entry.slug}
            onClick={() => { setSelected(entry.slug); setView({ kind: 'history' }); }}
          >
            <span className={`dot ${entry.lastJob?.state ?? 'none'}`} />
            <span className="client-lines">
              <span className="client-name">{entry.appName}</span>
              <span className="client-pkg">{entry.packageName}</span>
              <span className="client-next">next build {entry.nextVersionCode}</span>
            </span>
          </button>
        ))}

        {clients.length === 0 && !fatal && (
          <p style={{ padding: '4px 26px', color: 'var(--ink-3)' }}>No clients yet.</p>
        )}

        <div className="rail-foot">
          {client && (
            <BuildForm
              client={client}
              template={template}
              platforms={platforms}
              macs={macs}
              onBuild={(options) => void build(client.slug, options)}
            />
          )}
          <button type="button" className={client ? 'plain' : 'btn quiet'}
                  style={client ? { marginTop: 14 } : undefined}
                  onClick={() => setView({ kind: 'new-client' })}>
            Add client
          </button>
        </div>
      </nav>

      <main className="main">
        {fatal && (
          <div className="empty">
            <h1>Cannot reach the service</h1>
            <p>{fatal}</p>
          </div>
        )}

        {!fatal && view.kind === 'new-client' && (
          <NewClient
            onCancel={() => setView({ kind: 'history' })}
            onCreated={(slug) => {
              setSelected(slug);
              setView({ kind: 'history' });
              showToast(`${slug} added`);
              void reload();
            }}
          />
        )}

        {!fatal && view.kind === 'macs' && <Macs me={session.user} onToast={showToast} />}
        {!fatal && view.kind === 'people' && <People me={session.user} onToast={showToast} />}

        {!fatal && view.kind === 'storage' && (
          <StorageView storage={storage} onToast={showToast} onChanged={reload} />
        )}

        {!fatal && view.kind === 'job' && (
          <JobView
            jobId={view.id}
            appName={clients.find((entry) => entry.slug === jobs.find((job) => job.id === view.id)?.slug)?.appName
              ?? null}
            canRemove={isAdmin}
            onToast={showToast}
            onRemoved={() => { setView({ kind: 'history' }); void reload(); }}
          />
        )}

        {!fatal && view.kind === 'history' && (
          <div className="inner">
            <header className="run-head">
              <div className="run-title">
                <h1>{client ? client.appName : 'All builds'}</h1>
              </div>
              {!client && jobs.length > 0 && (
                <dl className="facts">
                  <div className="fact">
                    <dt>Clients</dt>
                    <dd>{clients.length}</dd>
                  </div>
                  <div className="fact">
                    <dt>Builds kept</dt>
                    <dd>{jobs.length}</dd>
                  </div>
                  <div className="fact prose">
                    <dt>Newest passing</dt>
                    <dd>{(() => {
                      const passed = jobs.find((job) => job.state === 'passed');
                      return passed ? `#${passed.id} ${ago(passed.queuedAt)}` : 'none yet';
                    })()}</dd>
                  </div>
                  <div className="fact prose">
                    <dt>Waiting</dt>
                    <dd>{queued === 0 ? 'nothing queued' : `${queued} build${queued === 1 ? '' : 's'}`}</dd>
                  </div>
                </dl>
              )}
              {client && (
                <ClientPanel
                  client={client}
                  template={template}
                  isAdmin={isAdmin}
                  onChanged={() => void reload()}
                  onToast={showToast}
                />
              )}
            </header>

            {visibleJobs.length === 0 ? (
              <div className="empty">
                <h1>{client ? 'Nothing built yet' : 'No builds yet'}</h1>
                <p>
                  {client
                    ? `Press Build to make ${client.versionName ?? 'the first release'} (${client.nextVersionCode}).`
                    : 'Pick a client on the left, then press Build.'}
                </p>
              </div>
            ) : (
              <section className="section">
                <div className="section-head">
                  <h2>{client ? 'Builds' : 'Newest first'}</h2>
                  <span className="note">{visibleJobs.length} shown</span>
                </div>
                <Register
                  jobs={visibleJobs}
                  showClient={!client}
                  names={new Map(clients.map((entry) => [entry.slug, entry.appName]))}
                  ago={ago}
                  onOpen={(id) => setView({ kind: 'job', id })}
                />
              </section>
            )}

            {/* Last on the page, after the history it destroys, not above it. */}
            {client && isAdmin && (
              <RemoveClient
                client={client}
                onToast={showToast}
                onRemoved={() => { setSelected(null); void reload(); showToast('Client removed'); }}
              />
            )}
          </div>
        )}
      </main>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
